import { Box, Stack, Typography } from '@mui/material';
import LockRounded from '@mui/icons-material/LockRounded';
import { SAMPLE_BRANDS } from '@/marketplace/brands';
import { PhoneMockup } from '@/marketplace/components/PhoneMockup';
import { toLabel } from '../domain';
import { useTicker } from '../motion';

// The center phone cycles through these; the side phones stay put.
const CYCLE = [SAMPLE_BRANDS[0], SAMPLE_BRANDS[3], SAMPLE_BRANDS[5], SAMPLE_BRANDS[4]];
const SIDES = [SAMPLE_BRANDS[1], SAMPLE_BRANDS[2]];

/** Students' apps in different brands: the product in one picture. */
export function HeroFan() {
  const step = useTicker(CYCLE.length, 2800);
  const focus = CYCLE[step];
  const phone = (offset: number, scale: { xs: number; sm: number }) => ({
    position: 'absolute' as const,
    top: 0,
    left: '50%',
    width: 300,
    transformOrigin: 'top center',
    transform: {
      xs: `translateX(calc(-50% + ${offset * 86}px)) rotate(${offset * 8}deg) scale(${scale.xs})`,
      sm: `translateX(calc(-50% + ${offset * 120}px)) rotate(${offset * 8}deg) scale(${scale.sm})`,
    },
  });

  return (
    <Box
      aria-label="Apps de alunos com marcas diferentes"
      role="img"
      sx={{ position: 'relative', height: { xs: 420, sm: 560 }, maxWidth: 520, mx: 'auto' }}
    >
      {SIDES.map((brand, i) => (
        <Box
          key={brand.name}
          aria-hidden
          sx={{
            ...phone(i === 0 ? -1 : 1, { xs: 0.58, sm: 0.74 }),
            top: { xs: 34, sm: 44 },
            zIndex: 1,
            opacity: 0.95,
          }}
        >
          <PhoneMockup brandName={brand.name} color={brand.color} radius={i === 0 ? 20 : 26} />
        </Box>
      ))}
      <Box
        aria-hidden
        sx={{
          ...phone(0, { xs: 0.66, sm: 0.84 }),
          zIndex: 2,
          animation: 'fan-float 6s ease-in-out infinite',
          '@keyframes fan-float': {
            '0%, 100%': { translate: '0 0' },
            '50%': { translate: '0 -8px' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <PhoneMockup brandName={focus.name} color={focus.color} radius={16} />
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          position: 'absolute',
          zIndex: 3,
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderRadius: 99,
          bgcolor: theme.vars.palette.background.paper,
          border: `1px solid ${theme.vars.palette.divider}`,
          boxShadow: `0 16px 40px -18px ${theme.alpha('#000', 0.35)}`,
          whiteSpace: 'nowrap',
        })}
      >
        <LockRounded sx={{ fontSize: 16, color: 'success.main' }} />
        <Typography
          key={focus.name}
          sx={{
            fontSize: 15,
            fontWeight: 600,
            animation: 'addr-in 500ms ease',
            '@keyframes addr-in': { from: { opacity: 0, transform: 'translateY(6px)' } },
          }}
        >
          {toLabel(focus.name)}.app
        </Typography>
      </Stack>
    </Box>
  );
}
